import {
  Activity,
  BatteryCharging,
  BatteryHalf,
  ClockHistory,
  Cpu,
  Dot,
  ExclamationTriangle,
  GeoAlt,
  Radioactive,
  Reception4,
  Telephone,
} from "react-bootstrap-icons";
import styles from "./DeviceSections.module.css";

export const StatCard = ({
  icon,
  label,
  value,
  accentClassName,
}) => {
  return (
    <div className={styles.statCard}>
      <div className={`${styles.statIcon} ${accentClassName || styles.accentBlue}`}>{icon}</div>
      <div className={styles.statMeta}>
        <p className={styles.statLabel}>{label}</p>
        <p className={styles.statValue}>{value}</p>
      </div>
    </div>
  );
};

function boolToState(value, trueText, falseText) {
  if (value == null) return "-";
  return value ? trueText : falseText;
}

function getLastLocation(status, location) {
  if (status.last_location) return status.last_location;

  if (!Array.isArray(location) || location.length === 0) return "-";

  const latest = location[0];
  const lat = Number(latest?.lat);
  const lng = Number(latest?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
  return "-";
}

export default function DeviceInfoSection({ device, status, settings, location = [] }) {
  const lastOnline = new Date(status.last_online_time);
  const formattedDate = Number.isNaN(lastOnline.getTime())
    ? "-"
    : lastOnline.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

  const batteryColor =
    status.battery_level == null
      ? styles.accentSlate
      : status.battery_level > 50
      ? styles.accentGreen
      : status.battery_level > 20
        ? styles.accentAmber
        : styles.accentRed;

  const batteryValue =
    status.battery_level == null
      ? "-"
      : (
        <span className={styles.batteryWrap}>
          {status.battery_level}%
          <span className={styles.batteryTrack}>
            <span
              className={`${styles.batteryFill} ${
                status.battery_level > 50
                  ? styles.fillGreen
                  : status.battery_level > 20
                    ? styles.fillAmber
                    : styles.fillRed
              }`}
              style={{ width: `${status.battery_level}%` }}
            />
          </span>
        </span>
      );

  const signalStrengthValue =
    status.signal_strength == null ? "-" : `${status.signal_strength} dBm`;

  const deviceStatus = status.device_status || (status.online ? "Online" : "Offline");
  const lastLocation = getLastLocation(status, location);
  const motionAlertEnabled =
    settings?.motion_alert?.enable == null
      ? status.motion_detected
      : settings.motion_alert.enable;
  const fallDetectAlertEnabled =
    settings?.fall_detect_alert?.enable
    ?? settings?.falldown_alert?.enable
    ?? status.fall_detected;

  return (
    <div className={styles.infoRoot}>
      <div className={styles.infoHeaderCard}>
        <div className={styles.infoHeaderLeft}>
          <p className={styles.infoHeaderTag}>Device User</p>
          <h2 className={styles.infoHeaderName}>{settings.full_name || "-"}</h2>
          <p className={styles.infoHeaderAddress}>{settings.address || "-"}</p>
        </div>
        <div className={styles.infoHeaderRight}>
          <span
            className={`${styles.onlinePill} ${status.online ? styles.online : styles.offline}`}
          >
            <Dot size={14} />
            {status.online ? "Online" : "Offline"}
          </span>
          <p className={styles.infoHeaderPersonMeta}>
            {settings.gender || "-"}
            {settings.age != null ? `, ${settings.age} yrs` : ""}
          </p>
        </div>
      </div>

      <div>
        <p className={styles.sectionLabel}>Device Details</p>
        <div className={styles.statsGrid}>
          <StatCard
            icon={<Cpu size={16} />}
            label="Model"
            value={device.model || "-"}
            accentClassName={styles.accentBlue}
          />
          <StatCard
            icon={<Radioactive size={16} />}
            label="IMEI"
            value={device.imei || "-"}
            accentClassName={styles.accentSlate}
          />
          <StatCard
            icon={<Telephone size={16} />}
            label="SIM Number"
            value={device.sim_number || "-"}
            accentClassName={styles.accentTeal}
          />
          <StatCard
            icon={
              status.in_charging ? (
                <BatteryCharging size={16} />
              ) : (
                <BatteryHalf size={16} />
              )
            }
            label="Battery"
            value={batteryValue}
            accentClassName={batteryColor}
          />
          <StatCard
            icon={<Reception4 size={16} />}
            label="Signal Strength"
            value={signalStrengthValue}
            accentClassName={styles.accentTeal}
          />
          <StatCard
            icon={<GeoAlt size={16} />}
            label="GPS"
            value={status.gps ? "Enabled" : "Disabled"}
            accentClassName={status.gps ? styles.accentGreen : styles.accentSlate}
          />
          <StatCard
            icon={<ClockHistory size={16} />}
            label="Last Online"
            value={formattedDate}
            accentClassName={styles.accentBlue}
          />
          <StatCard
            icon={<Activity size={16} />}
            label="Motion"
            value={boolToState(motionAlertEnabled, "Enabled", "Disabled")}
            accentClassName={motionAlertEnabled ? styles.accentGreen : styles.accentSlate}
          />
          <StatCard
            icon={<ExclamationTriangle size={16} />}
            label="Fall Detect"
            value={boolToState(fallDetectAlertEnabled, "Enabled", "Disabled")}
            accentClassName={fallDetectAlertEnabled ? styles.accentGreen : styles.accentSlate}
          />
          <StatCard
            icon={status.in_charging ? <BatteryCharging size={16} /> : <BatteryHalf size={16} />}
            label="In Charging"
            value={status.in_charging ? "Yes" : "No"}
            accentClassName={status.in_charging ? styles.accentGreen : styles.accentSlate}
          />
          <StatCard
            icon={<Reception4 size={16} />}
            label="Cellular Mode"
            value={status.cellular_mode || "-"}
            accentClassName={styles.accentBlue}
          />
          <StatCard
            icon={<GeoAlt size={16} />}
            label="Last Location"
            value={lastLocation}
            accentClassName={styles.accentTeal}
          />
        </div>
      </div>
    </div>
  );
}
